import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginForm from '../../components/Auth/LoginForm';
import { useAuth } from '../../contexts/AuthContext';

const WELCOME_SEEN_KEY = 'welcome_new_user_seen';

interface LoginData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

declare global {
  interface Window {
    fbq?: (action: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}

const WelcomeNewUserPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [ready, setReady] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fromRegister = searchParams.get('from') === 'register';
    const alreadySeen = typeof window !== 'undefined' && localStorage.getItem(WELCOME_SEEN_KEY);

    if (!fromRegister) {
      navigate('/login', { replace: true });
      return;
    }
    if (alreadySeen) {
      navigate('/app/dashboard', { replace: true });
      return;
    }

    setReady(true);

    // Disparar evento de conversão Meta Pixel (CompleteRegistration)
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'CompleteRegistration');
      } catch {
        // ignorar se pixel não estiver configurado
      }
    }
  }, [searchParams, navigate]);

  const handleLogin = async (data: LoginData) => {
    setIsLoading(true);
    setError('');

    try {
      await login(data.email, data.password, data.rememberMe ?? true);
      if (typeof window !== 'undefined') {
        localStorage.setItem(WELCOME_SEEN_KEY, 'true');
      }
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <p className="text-center text-lg text-foreground font-medium mb-2">
          Seja muito bem-vindo(a) à Plataforma EscriturIA.
        </p>
        <p className="text-center text-base text-muted-foreground mb-6">
          Faça seu login
        </p>
        <LoginForm
          onSubmit={handleLogin}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </div>
  );
};

export default WelcomeNewUserPage;
